import React, { useMemo, useState } from 'react';
import type { DateRange, MonthSeries, RangeMetrics, SeriesSet } from '../../types/dashboard';
import { Panel } from '../ui/Panel';
import { SortableTh } from '../ui/SortableTh';
import { ExportButton } from '../ui/TableControls';
import { EmptyState } from '../ui/EmptyState';
import { useSort } from '../../hooks/useSort';
import { monthLabel, rangeMonths } from '../../utils/calendar';
import { perUnitRange, rangeMetrics } from '../../utils/metrics';
import { change, formatChange, formatValue, isCountMetric, metricMeta, monthValue, rangeValue, type TrendMetric } from '../../utils/metricConfig';
import { fmtCurrency, fmtInt, fmtRatio } from '../../utils/format';
import { downloadCsv } from '../../utils/csv';
interface Stats {
  members: number;
  converted: number;
  registrations: number;
  revenue: number;
  digital: number | null;
  local: number | null;
}
interface ExtraCol {
  key: string;
  label: string;
  get: (s: Stats) => number | null;
  fmt: (v: number) => string;
}
const div = (a: number, b: number) => b ? a / b : null;
function extrasFor(metric: TrendMetric): ExtraCol[] {
  const f = (v: number) => formatValue(metric, v);
  switch (metric) {
    case 'members':
      return [{
        key: 'digital',
        label: 'Digital',
        get: s => s.digital,
        fmt: f
      }, {
        key: 'local',
        label: 'Local',
        get: s => s.local,
        fmt: f
      }];
    case 'registrations':
      return [{
        key: 'converted',
        label: 'Unique members registered',
        get: s => s.converted,
        fmt: fmtInt
      }, {
        key: 'rpm',
        label: 'Regs / member',
        get: s => div(s.registrations, s.converted),
        fmt: v => fmtRatio(v)
      }];
    case 'revenue':
      return [{
        key: 'registrations',
        label: 'Registrations',
        get: s => s.registrations,
        fmt: fmtInt
      }, {
        key: 'rpr',
        label: 'Revenue / registration',
        get: s => div(s.revenue, s.registrations),
        fmt: fmtCurrency
      }];
    case 'conversion':
      return [{
        key: 'members',
        label: 'Members',
        get: s => s.members,
        fmt: fmtInt
      }, {
        key: 'converted',
        label: 'Converted members',
        get: s => s.converted,
        fmt: fmtInt
      }];
    case 'regsPerMember':
      return [{
        key: 'registrations',
        label: 'Registrations',
        get: s => s.registrations,
        fmt: fmtInt
      }, {
        key: 'converted',
        label: 'Contributing members',
        get: s => s.converted,
        fmt: fmtInt
      }];
  }
}
interface Row {
  m: number;
  label: string;
  values: Record<string, number | null>;
}
interface MonthlyTableProps {
  metric: TrendMetric;
  series: SeriesSet;
  network: SeriesSet;
  range: DateRange;
  scopeUnits: (m: number) => number;
  networkUnits: (m: number) => number;
  onMonthClick: (m: number) => void;
}
type RowFilter = 'all' | 'yoyDown' | 'yoyUp';
export function MonthlyTable({
  metric,
  series,
  network,
  range,
  scopeUnits,
  networkUnits,
  onMonthClick
}: MonthlyTableProps) {
  const [rowFilter, setRowFilter] = useState<RowFilter>('all');
  const count = isCountMetric(metric);
  const meta = metricMeta(metric);
  const extras = useMemo(() => extrasFor(metric), [metric]);
  const fmt = (v: number) => formatValue(metric, v);
  const monthStats = (s: MonthSeries, d: MonthSeries, l: MonthSeries, m: number): Stats => ({
    members: s.members[m],
    converted: s.conv[0][m],
    registrations: s.regs[0][m],
    revenue: s.rev[0][m],
    digital: monthValue(metric, d, m),
    local: monthValue(metric, l, m)
  });
  const periodStats = (r: RangeMetrics): Stats => ({
    members: r.members,
    converted: r.registeringMembers,
    registrations: r.registrations,
    revenue: r.revenue,
    digital: rangeValue(metric, rangeMetrics(series.Digital, range.start, range.end)),
    local: rangeValue(metric, rangeMetrics(series.Local, range.start, range.end))
  });
  const rows = useMemo<Row[]>(() => rangeMonths(range).map(m => {
    const total = monthValue(metric, series.all, m);
    const active = scopeUnits(m);
    const st = monthStats(series.all, series.Digital, series.Local, m);
    const values: Record<string, number | null> = {
      m,
      active,
      total,
      avg: count ? active && total !== null ? total / active : null : monthValue(metric, network.all, m),
      mom: change(metric, total, monthValue(metric, series.all, m - 1)),
      yoy: change(metric, total, monthValue(metric, series.all, m - 12))
    };
    extras.forEach(c => values[c.key] = c.get(st));
    return {
      m,
      label: monthLabel(m),
      values
    };
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [metric, series, network, range, extras]);
  const visible = useMemo(() => rows.filter(r => rowFilter === 'all' || (rowFilter === 'yoyDown' ? (r.values.yoy ?? 0) < 0 : (r.values.yoy ?? 0) > 0)), [rows, rowFilter]);
  const {
    sorted,
    sort,
    onSort
  } = useSort<Row, string>(visible, {
    key: 'm',
    dir: 'desc'
  }, (r, k) => r.values[k] ?? null);
  const period = rangeMetrics(series.all, range.start, range.end);
  const periodTotal = rangeValue(metric, period);
  const activeMonthCount = rangeMonths(range).filter(m => scopeUnits(m) > 0).length;
  // Counts: mean of the monthly (total ÷ active territories) values — never a 13-month sum.
  const periodAvg = count ? activeMonthCount ? rangeValue(metric, perUnitRange(series.all, range.start, range.end, scopeUnits)) / activeMonthCount : 0 : rangeValue(metric, rangeMetrics(network.all, range.start, range.end));
  const pStats = periodStats(period);
  const totalLabel = metric === 'members' ? 'New members' : count ? `Total ${meta.label.toLowerCase()}` : meta.label;
  const avgLabel = count ? 'Avg / active territory' : 'Network';
  const changeCell = (v: number | null) => v === null ? <span className="text-ink-subtle" data-id="element-307" data-mp="zbii44fim8p4">—</span> : <span className={v < 0 ? 'text-negative' : 'text-positive'} data-id="element-308" data-mp="za75rg807sme">{formatChange(metric, v)}</span>;
  const exportCsv = () => {
    const pct = (v: number | null) => v === null ? '' : (v * 100).toFixed(2);
    downloadCsv(`i9-monthly-${metric}-${range.label.replace(/[^a-z0-9]+/gi, '-')}.csv`, ['Month', 'Active Territories', totalLabel, avgLabel, metric === 'conversion' ? 'MoM vs prior month (pp)' : 'MoM vs prior month %', metric === 'conversion' ? 'YoY vs same month last year (pp)' : 'YoY vs same month last year %', ...extras.map(e => e.label)], sorted.map(r => [r.label, r.values.active ?? '', r.values.total === null ? '' : Number(r.values.total.toFixed(4)), r.values.avg === null ? '' : Number(r.values.avg.toFixed(4)), pct(r.values.mom), pct(r.values.yoy), ...extras.map(e => r.values[e.key] === null ? '' : Number((r.values[e.key] as number).toFixed(4)))]));
  };
  return <Panel title={`Monthly ${meta.label.toLowerCase()}`} definitionId={meta.definitionId} subtitle={metric === 'members' ? 'New members acquired each month, by acquisition date. Click a row for month detail.' : 'Each row counts members added that month and their same-month registrations. Click a row for month detail.'} actions={<>
          <label className="flex items-center gap-1.5 text-xs text-ink-muted" data-id="element-310" data-mp="25691hrddpgym">
            Show
            <select value={rowFilter} onChange={e => setRowFilter(e.target.value as RowFilter)} className="h-8 rounded-md border border-line bg-white px-2 text-xs text-ink focus:border-navy/50 focus:outline-none" data-id="element-311" data-mp="2075x89583erp">
              <option value="all" data-id="element-312" data-mp="1qhmbmvq4l6t5">All months</option>
              <option value="yoyDown" data-id="element-313" data-mp="1lj1a8sz50h34">YoY decline only</option>
              <option value="yoyUp" data-id="element-314" data-mp="1lgf50zzpydo2">YoY growth only</option>
            </select>
          </label>
          <ExportButton onClick={exportCsv} data-id="element-315" data-mp="1hihffw21dpk5" />
        </>} bodyClassName="p-0" data-id="element-309" data-mp="2lolssl9bfp0l">
      {sorted.length === 0 ? <EmptyState title="No months match this filter" description="Switch the row filter back to All months." data-id="element-316" data-mp="30drjicxdiqqj" /> : <div className="max-h-[420px] overflow-auto" data-id="element-317" data-mp="35cb3i7sepsg9">
          <table className="w-full min-w-[920px] border-collapse text-[12.5px]" data-id="element-318" data-mp="2rdl7ywl8k0u0">
            <thead className="sticky top-0 z-10 bg-navy-50 text-navy" data-id="element-319" data-mp="2rfnwtdanjnx5">
              <tr data-id="element-320" data-mp="2vdlmsic8ye1l">
                <SortableTh label="Month" sortKey="m" sort={sort} onSort={onSort} align="left" className="pl-5" data-id="element-321" data-mp="2vh9hhktfh6hw" />
                <SortableTh label="Active territories" sortKey="active" sort={sort} onSort={onSort} data-id="element-322" data-mp="2hlyoye2noxgp" />
                <SortableTh label={totalLabel} sortKey="total" sort={sort} onSort={onSort} data-id="element-323" data-mp="2mgb8sp81oasz" />
                <SortableTh label={avgLabel} sortKey="avg" sort={sort} onSort={onSort} data-id="element-324" data-mp="27nr9y0rxgphc" />
                <SortableTh label="MoM vs prior mo" sortKey="mom" sort={sort} onSort={onSort} data-id="element-325" data-mp="2cmm4fzz9c2ha" />
                <SortableTh label="YoY vs same mo LY" sortKey="yoy" sort={sort} onSort={onSort} data-id="element-326" data-mp="1yr0iy2s8g0tm" />
                {extras.map((e, i) => <SortableTh key={e.key} label={e.label} sortKey={e.key} sort={sort} onSort={onSort} className={i === extras.length - 1 ? 'pr-5' : ''} data-id="element-327" data-mp="1k1p1r8hdlrra" data-mpi={i} />)}
              </tr>
            </thead>
            <tbody className="tabular-nums" data-id="element-328" data-mp="1t35e3lrr43y5">
              {sorted.map((r, i) => <tr key={r.m} tabIndex={0} onClick={() => onMonthClick(r.m)} onKeyDown={e => e.key === 'Enter' && onMonthClick(r.m)} className={`cursor-pointer border-b border-line transition-colors duration-100 hover:bg-navy-50 focus-visible:bg-navy-50 focus-visible:outline-none ${i % 2 ? 'bg-canvas/50' : ''}`} data-id="element-329" data-mp="1p1jw5m0d0zh3" data-mpi={i}>
                  <td className="whitespace-nowrap py-2 pl-5 pr-3 font-medium text-navy" data-id="element-330" data-mp="27suirqz1yq36">{r.label}</td>
                  <td className="px-3 py-2 text-right" data-id="element-331" data-mp="2lslzhzgyekkv">{r.values.active}</td>
                  <td className="px-3 py-2 text-right font-semibold" data-id="element-332" data-mp="2gszn1s8y98d4">{r.values.total === null ? '—' : fmt(r.values.total)}</td>
                  <td className="px-3 py-2 text-right text-ink-muted" data-id="element-333" data-mp="2bhuju7v7mdq0">{r.values.avg === null ? '—' : fmt(r.values.avg)}</td>
                  <td className="px-3 py-2 text-right" data-id="element-334" data-mp="26p1d5eg6kvba">{changeCell(r.values.mom)}</td>
                  <td className="px-3 py-2 text-right" data-id="element-335" data-mp="21rrgbdqgphdf">{changeCell(r.values.yoy)}</td>
                  {extras.map((e, j) => {
              const v = r.values[e.key];
              return <td key={e.key} className={`py-2 text-right ${j === extras.length - 1 ? 'pl-3 pr-5' : 'px-3'}`} data-id="element-336" data-mp="2uf5iprcxj54x" data-mpi={j}>
                        {v === null ? '—' : e.fmt(v)}
                      </td>;
            })}
                </tr>)}
            </tbody>
            <tfoot className="sticky bottom-0 bg-white tabular-nums" data-id="element-337" data-mp="2fieijfvflpe9">
              <tr className="border-t-2 border-navy/40 font-semibold text-navy" data-id="element-338" data-mp="193n9gow5my95">
                <td className="py-2.5 pl-5 pr-3" data-id="element-339" data-mp="142zlkc382xj5">Selected period</td>
                <td className="px-3 py-2.5 text-right text-ink-subtle" data-id="element-340" data-mp="145u581eewebi">—</td>
                <td className="px-3 py-2.5 text-right" data-id="element-341" data-mp="3fsnvjiycwpug">{fmt(periodTotal)}</td>
                <td className="px-3 py-2.5 text-right" data-id="element-342" data-mp="3kqphcbn6339m">
                  {fmt(periodAvg)}
                  {count && <span className="ml-1 text-[11px] font-normal text-ink-subtle" data-id="element-343" data-mp="3po8ut9c1jc0i">monthly mean</span>}
                </td>
                <td className="px-3 py-2.5 text-right text-ink-subtle" data-id="element-344" data-mp="3uh2141n5qsep">—</td>
                <td className="px-3 py-2.5 text-right text-ink-subtle" data-id="element-345" data-mp="38o3f2mhp09o">—</td>
                {extras.map((e, j) => {
              const v = e.get(pStats);
              return <td key={e.key} className={`py-2.5 text-right ${j === extras.length - 1 ? 'pl-3 pr-5' : 'px-3'}`} data-id="element-346" data-mp="cb5npbil3iu1" data-mpi={j}>
                      {v === null ? '—' : e.fmt(v)}
                    </td>;
            })}
              </tr>
            </tfoot>
          </table>
        </div>}
      <p className="border-t border-line px-5 py-2.5 text-[11.5px] text-ink-subtle" data-id="element-347" data-mp="2j1dbf2iirfa7">
        {metric === 'members' ? 'Selected-period New members = sum of monthly rows. Avg / active territory = that month’s new members ÷ active territories that month; the period value is the mean of the monthly averages. ' : `The period row also includes members who registered in a later month within the range, so it can differ from the sum of monthly rows. ${count ? 'Avg / active territory = monthly total ÷ active territories that month; the period value is a monthly mean.' : 'Network column shows the network rate for the same month.'} `}
        MoM compares each month to the prior month; YoY to the same month one year earlier. Oct ’26 is month-to-date and excluded.
      </p>
    </Panel>;
}